import React, { useState, useContext, useRef, useEffect } from 'react';
import { toJpeg } from 'html-to-image';
import Modal from './Modal';
import { ThemeContext } from '../contexts/ThemeContext';
import { ResultsContext } from '../contexts/ResultsContext';
import { useTournamentAppConfiguration } from '../utils/tournamentconfig/TournamentConfiguration';
import './CollageModal.css';

/**
 * Creates a placeholder base64 data URL if an image fails to load
 */
const createPlaceholderDataUrl = (title = '', rank = '') => {
  if (typeof document === 'undefined') return '';
  try {
    const canvas = document.createElement('canvas');
    canvas.width = 600;
    canvas.height = 400;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      const gradient = ctx.createLinearGradient(0, 0, 600, 400);
      gradient.addColorStop(0, '#1f2937');
      gradient.addColorStop(1, '#111827');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, 600, 400);
      
      if (title || rank) {
        ctx.fillStyle = '#ffffff';
        ctx.font = 'bold 32px Arial';
        ctx.textAlign = 'center';
        const text = rank ? `#${rank} - ${title}` : title;
        ctx.fillText(text, 300, 200);
      }
      
      return canvas.toDataURL('image/png');
    }
  } catch (e) {
    // Ignore canvas errors
  }
  return '';
};

/**
 * Format tournament date string
 */
const formatTournamentDate = (dateString) => {
  if (!dateString) return '';
  try {
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return dateString;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch (e) {
    return dateString;
  }
};

/**
 * CollageModal component displays a modal with a collage of game images using conventional HTML/CSS
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Function} props.onClose - Function to call when the modal is closed
 * @param {string} props.username - Username of the player
 * @param {string} props.overallRank - Player's overall rank in the tournament
 */
function CollageModal({ isOpen, onClose, username, overallRank }) {
  const { theme } = useContext(ThemeContext);
  const { results } = useContext(ResultsContext);
  const config = useTournamentAppConfiguration();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [shareMessage, setShareMessage] = useState('');
  const [isCopied, setIsCopied] = useState(false);
  const [showScores, setShowScores] = useState(true);
  const [showPlayerCount, setShowPlayerCount] = useState(false);
  const [showTournamentDates, setShowTournamentDates] = useState(true);
  const [showCreatedDate, setShowCreatedDate] = useState(true);
  const [userGames, setUserGames] = useState([]);
  const [hiddenGameTitles, setHiddenGameTitles] = useState([]);
  const [isDownloading, setIsDownloading] = useState(false);
  const [previewScale, setPreviewScale] = useState(1);
  const [cardHeight, setCardHeight] = useState(0);
  
  const collageRef = useRef(null);
  const previewWrapperRef = useRef(null);

  const toggleHideGame = (gameTitle) => {
    setHiddenGameTitles(prev => 
      prev.includes(gameTitle) 
        ? prev.filter(t => t !== gameTitle) 
        : [...prev, gameTitle]
    );
  };

  const unhideAllGames = () => {
    setHiddenGameTitles([]);
  };

  // Calculate scaling factor so the 800px collage card scales smoothly on small viewports
  useEffect(() => {
    if (!isOpen) return;

    const updateScale = () => {
      if (previewWrapperRef.current) {
        const containerWidth = previewWrapperRef.current.clientWidth;
        const targetWidth = 800;
        if (containerWidth > 0 && containerWidth < targetWidth) {
          setPreviewScale(containerWidth / targetWidth);
        } else {
          setPreviewScale(1);
        }
      }
      if (collageRef.current) {
        setCardHeight(collageRef.current.offsetHeight);
      }
    };

    updateScale();

    let resizeObserver;
    if (typeof ResizeObserver !== 'undefined' && previewWrapperRef.current) {
      resizeObserver = new ResizeObserver(() => updateScale());
      resizeObserver.observe(previewWrapperRef.current);
    }

    window.addEventListener('resize', updateScale);
    return () => {
      if (resizeObserver) resizeObserver.disconnect();
      window.removeEventListener('resize', updateScale);
    };
  }, [isOpen, userGames, hiddenGameTitles, showScores, showPlayerCount, showTournamentDates, showCreatedDate]);

  // Filter and prepare ranked games when modal opens
  useEffect(() => {
    if (!isOpen) return;

    if (!results || !results.games || !username) {
      setError('No game data available or username not provided');
      setLoading(false);
      return;
    }
    
    setLoading(true);
    setError(null);

    const games = [];
    results.games.forEach(game => {
      const userEntry = game.leaderboard?.find(entry => 
        entry.userName.toLowerCase() === username.toLowerCase()
      );
      
      if (userEntry) {
        games.push({
          title: game.title,
          image: game.image,
          rank: userEntry.rank,
          score: userEntry.score,
          initials: userEntry.initials,
          totalPlayers: game.leaderboard?.length || 0
        });
      }
    });

    if (games.length === 0) {
      setError('No ranked games found for this user');
      setLoading(false);
      return;
    }

    // Sort by rank (best rank first)
    games.sort((a, b) => a.rank - b.rank);
    setUserGames(games);

    // Setup share message
    const baseUrl = window.location.origin + window.location.pathname;
    const directLink = `${baseUrl}?username=${encodeURIComponent(username)}&tournamentId=${encodeURIComponent(results.tournamentId)}`;
    const userOverallRank = overallRank || 'Unranked';
    const shareText = config.showOverallRankInImagecollage
      ? `Check out my ${results.title} results! ${username} - ${userOverallRank} \n${directLink}`
      : `Check out my ${results.title} results! ${username} \n${directLink}`;
    setShareMessage(shareText);

    setLoading(false);
  }, [isOpen, username, results, overallRank, config.showOverallRankInImagecollage]);

  // Handle image download using html-to-image
  const handleDownload = async () => {
    if (!collageRef.current) return;
    setIsDownloading(true);
    try {
      if (typeof document !== 'undefined' && document.fonts && document.fonts.ready) {
        await document.fonts.ready;
      }

      if (collageRef.current) {
        collageRef.current.classList.add('is-exporting');
      }

      const dataUrl = await toJpeg(collageRef.current, {
        quality: 0.85,
        pixelRatio: 1.5,
        backgroundColor: '#121212',
        cacheBust: true
      });

      if (collageRef.current) {
        collageRef.current.classList.remove('is-exporting');
      }

      const link = document.createElement('a');
      link.download = `${username}-tournament-collage.jpg`;
      link.href = dataUrl;
      link.click();
    } catch (err) {
      if (collageRef.current) {
        collageRef.current.classList.remove('is-exporting');
      }
      console.error('Error generating image download:', err);
      alert('Failed to download collage image. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  // Handle copy share message
  const handleCopyShareMessage = () => {
    if (!shareMessage) return;
    
    navigator.clipboard.writeText(shareMessage)
      .then(() => {
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 2000);
      })
      .catch(err => {
        console.error('Failed to copy text:', err);
        alert('Failed to copy text. Please try again.');
      });
  };

  // Handle social media sharing
  const handleShare = (platform) => {
    if (!shareMessage) return;
    
    let shareUrl;
    const directLink = shareMessage.split('\n')[1];
    
    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(directLink)}&quote=${encodeURIComponent(shareMessage)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareMessage)}`;
        break;
      case 'discord':
        shareUrl = `https://discord.com/app`;
        navigator.clipboard.writeText(shareMessage)
          .then(() => {
            alert('Share message copied to clipboard. Paste it in Discord!');
          })
          .catch(err => {
            console.error('Failed to copy text:', err);
            alert('Failed to copy text. Please copy the message manually and share it on Discord.');
          });
        break;
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodeURIComponent(shareMessage)}`;
        break;
      default:
        return;
    }
    
    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  const userOverallRank = overallRank || 'Unranked';
  const userDisplayText = config.showOverallRankInImagecollage 
    ? `${username} - ${userOverallRank}`
    : username;

  const startDateStr = formatTournamentDate(results?.start || results?.startDate);
  const endDateStr = formatTournamentDate(results?.end || results?.endDate);
  const tournamentDates = (startDateStr && endDateStr) 
    ? `${startDateStr} — ${endDateStr}` 
    : (startDateStr || endDateStr || '');

  const visibleUserGames = userGames.filter(game => !hiddenGameTitles.includes(game.title));

  const bestGame = visibleUserGames.length > 0 ? visibleUserGames[0] : null;
  const otherGames = visibleUserGames.slice(1);

  const siteUrl = typeof window !== 'undefined' ? window.location.origin : '';
  const timestamp = new Date().toLocaleString();

  const scaledMarginBottom = previewScale < 1 && cardHeight > 0 
    ? `-${(1 - previewScale) * cardHeight}px` 
    : '0px';

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose} 
      title="Tournament Collage"
      size="large"
    >
      <div className={`collage-container ${theme}`}>
        {loading && (
          <div className="collage-loading">
            <i className="fa-solid fa-spinner fa-spin" style={{ marginRight: '8px' }}></i>
            Generating collage...
          </div>
        )}

        {error && (
          <div className="collage-error">{error}</div>
        )}

        {!loading && !error && userGames.length > 0 && (
          <>
            {/* Conventional HTML Collage Display */}
            <div className="collage-preview-container" ref={previewWrapperRef}>
              <div 
                className="collage-scale-wrapper"
                style={{
                  transform: `scale(${previewScale})`,
                  transformOrigin: 'top center',
                  marginBottom: scaledMarginBottom
                }}
              >
                <div className="collage-card" ref={collageRef}>
                  {/* Header */}
                  <div className="collage-header">
                    <h2 className="collage-tournament-title">{results?.title}</h2>
                    <div className="collage-user-info">{userDisplayText}</div>
                    {showTournamentDates && tournamentDates && (
                      <div className="collage-tournament-dates">{tournamentDates}</div>
                    )}
                  </div>

                  {/* Featured Best Game */}
                  {bestGame && (
                    <div className="collage-featured-card">
                      <div className="collage-featured-image-wrapper">
                        <img 
                          src={bestGame.image} 
                          alt={bestGame.title}
                          className="collage-featured-image" 
                          crossOrigin="anonymous"
                          onError={(e) => {
                            e.target.src = createPlaceholderDataUrl(bestGame.title, bestGame.rank);
                          }}
                        />
                        <div className="collage-featured-overlay" />
                        <button 
                          className="hide-game-button" 
                          onClick={() => toggleHideGame(bestGame.title)}
                          title="Hide this game from collage"
                          aria-label={`Hide ${bestGame.title}`}
                        >
                          <i className="fa-solid fa-eye-slash"></i>
                        </button>
                        <div className="collage-featured-badge">
                          <span>🏆</span>
                          <span>#{bestGame.rank}{showPlayerCount && bestGame.totalPlayers > 0 && ` of ${bestGame.totalPlayers}`}</span>
                        </div>
                        <div className="collage-featured-details">
                          <h3 className="collage-featured-game-title">{bestGame.title}</h3>
                          {showScores && bestGame.score != null && (
                            <div className="collage-featured-score">
                              {bestGame.score.toLocaleString()}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Other Games Grid */}
                  {otherGames.length > 0 && (
                    <div className="collage-games-grid">
                      {otherGames.map((game, idx) => (
                        <div key={idx} className="collage-game-card">
                          <div className="collage-game-image-wrapper">
                            <img 
                              src={game.image} 
                              alt={game.title}
                              className="collage-game-image" 
                              crossOrigin="anonymous"
                              onError={(e) => {
                                e.target.src = createPlaceholderDataUrl(game.title, game.rank);
                              }}
                            />
                            <div className="collage-game-overlay" />
                            <button 
                              className="hide-game-button" 
                              onClick={() => toggleHideGame(game.title)}
                              title="Hide this game from collage"
                              aria-label={`Hide ${game.title}`}
                            >
                              <i className="fa-solid fa-eye-slash"></i>
                            </button>
                            <div className="collage-game-rank-badge">
                              #{game.rank}{showPlayerCount && game.totalPlayers > 0 && ` of ${game.totalPlayers}`}
                            </div>
                            <div className="collage-game-info">
                              <div className="collage-game-title">{game.title}</div>
                              {showScores && game.score != null && (
                                <div className="collage-game-score">
                                  {game.score.toLocaleString()}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Footer Bar */}
                  <div className="collage-footer">
                    <span className="collage-branding">Create your own collage at {siteUrl}</span>
                    {showCreatedDate && <span className="collage-timestamp">{timestamp}</span>}
                  </div>
                </div>
              </div>
            </div>

            {/* Hidden Games Restoration UI */}
            {hiddenGameTitles.length > 0 && (
              <div className="hidden-games-manager">
                <div className="hidden-games-header">
                  <span>Hidden Games ({hiddenGameTitles.length})</span>
                  <button className="unhide-all-button" onClick={unhideAllGames}>
                    Unhide All
                  </button>
                </div>
                <div className="hidden-games-chips">
                  {hiddenGameTitles.map((title, idx) => (
                    <button 
                      key={idx} 
                      className="hidden-game-chip" 
                      onClick={() => toggleHideGame(title)}
                      title="Click to restore game"
                    >
                      <span>{title}</span>
                      <i className="fa-solid fa-plus"></i>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Modal Controls */}
            <div className="collage-actions">
              <button 
                className="download-button"
                onClick={handleDownload}
                disabled={isDownloading}
                style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}
              >
                <i className={`fa-solid ${isDownloading ? 'fa-spinner fa-spin' : 'fa-download'}`}></i>
                {isDownloading ? 'Generating Image...' : 'Download Image'}
              </button>

              <div className="collage-options">
                <label className="toggle-switch-container">
                  <span className="toggle-switch-label">Show Scores</span>
                  <div className="toggle-switch">
                    <input 
                      type="checkbox" 
                      checked={showScores} 
                      onChange={(e) => setShowScores(e.target.checked)} 
                    />
                    <span className="toggle-switch-slider"></span>
                  </div>
                </label>

                <label className="toggle-switch-container">
                  <span className="toggle-switch-label">Show Player Count</span>
                  <div className="toggle-switch">
                    <input 
                      type="checkbox" 
                      checked={showPlayerCount} 
                      onChange={(e) => setShowPlayerCount(e.target.checked)} 
                    />
                    <span className="toggle-switch-slider"></span>
                  </div>
                </label>

                <label className="toggle-switch-container">
                  <span className="toggle-switch-label">Show Tournament Dates</span>
                  <div className="toggle-switch">
                    <input 
                      type="checkbox" 
                      checked={showTournamentDates} 
                      onChange={(e) => setShowTournamentDates(e.target.checked)} 
                    />
                    <span className="toggle-switch-slider"></span>
                  </div>
                </label>

                <label className="toggle-switch-container">
                  <span className="toggle-switch-label">Show Created Date</span>
                  <div className="toggle-switch">
                    <input 
                      type="checkbox" 
                      checked={showCreatedDate} 
                      onChange={(e) => setShowCreatedDate(e.target.checked)} 
                    />
                    <span className="toggle-switch-slider"></span>
                  </div>
                </label>
              </div>
            </div>

            {/* Social Sharing */}
            <div className="social-share-container">
              <h3>Share to Social Media</h3>
              
              <div className="share-message-container">
                <input 
                  type="text" 
                  value={shareMessage} 
                  readOnly 
                  className="share-message-field" 
                  placeholder="Share message will appear here"
                />
                <button 
                  className={`copy-button ${isCopied ? 'copied' : ''}`}
                  onClick={handleCopyShareMessage}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}
                >
                  <i className={isCopied ? "fa-solid fa-check" : "fa-solid fa-copy"}></i>
                  <span>{isCopied ? 'Copied!' : 'Copy'}</span>
                </button>
              </div>
              
              <div className="social-buttons">
                <button 
                  className="social-button facebook-button"
                  onClick={() => handleShare('facebook')}
                  disabled={loading || !!error}
                >
                  <i className="fa-brands fa-facebook social-icon"></i>
                  Facebook
                </button>
                
                <button 
                  className="social-button twitter-button"
                  onClick={() => handleShare('twitter')}
                  disabled={loading || !!error}
                >
                  <i className="fa-brands fa-x-twitter social-icon"></i>
                  Twitter
                </button>
                
                <button 
                  className="social-button discord-button"
                  onClick={() => handleShare('discord')}
                  disabled={loading || !!error}
                >
                  <i className="fa-brands fa-discord social-icon"></i>
                  Discord
                </button>
                
                <button 
                  className="social-button whatsapp-button"
                  onClick={() => handleShare('whatsapp')}
                  disabled={loading || !!error}
                >
                  <i className="fa-brands fa-whatsapp social-icon"></i>
                  WhatsApp
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

export default CollageModal;